import * as _ from 'lodash';
import { useCallback } from 'react';

import { COUNT_MAX } from '../../../../../shared/dependenciesV2';
import { renderPackage } from '../../../../../shared/helpers/renderLocator';
import * as promises from '../../../../../shared/promises';
import { useAsyncRequest } from '../../../../hooks';
import { get } from '../../../../redux/api';
import { nuc } from '../../../../utils';
import type { UnknownDependency } from '../../../../types';

type UseDependencyPath = {
  root: string;
  target: string;
};

export type Path = {
  locator: string;
  title: string;
};

export type MinimalDependencyLock = {
  locator: string;
  originPaths: string[];
  // We don't have this data for unknown deps, so this is optional
  project?: {
    title: string;
  };
};

// This is the response from our API that we will conver to camelCase
// eslint-disable-next-line @typescript-eslint/naming-convention
type RawDependencyLock = Omit<MinimalDependencyLock, 'originPaths'> & { origin_paths: string[] };

type DependencyPathResponse = {
  paths: Path[][];
  dependencyLocks: Record<string, MinimalDependencyLock>;
  isLoading: boolean;
  errors: string[] | null;
};

function fetchPaths(root: string, target: string) {
  return get<Path[][][]>(`/api/revisions/${nuc(root)}/paths`, false, {
    locator: nuc(target),
    offset: 0,
    amount: 30,
  });
}

function fetchUnknownDependencies(root: string) {
  return get<UnknownDependency[]>(`/api/revisions/${nuc(root)}/unknown-dependencies`, false);
}

async function fetchDependencyPathsAndUnknownDeps(root: string, target: string) {
  const [{ data: paths }, { data: unknownDependencies }] = await Promise.all([
    fetchPaths(root, target),
    fetchUnknownDependencies(root),
  ]);

  return {
    paths: paths || [],
    unknownDependencies: unknownDependencies || [],
  };
}

// size of chunk of dependencies to fetch at a time; set to the COUNT_MAX value shared by both v1 and v2 APIs
const DEPENDENCY_LOCK_FETCH_SIZE = COUNT_MAX;

// fetch DependencyLocks for the given locators, DEPENDENCY_LOCK_FETCH_SIZE at a time
// we externally page to limit the size of the generated query string
function fetchDependencyLocks(root: string, locators: string[]) {
  return promises.flatMap(_.chunk(locators, DEPENDENCY_LOCK_FETCH_SIZE), async (locatorsChunk) => {
    const { data } = await get<RawDependencyLock[]>(`/api/revisions/${nuc(root)}/dependencies`, false, {
      includeLocators: locatorsChunk.map(l => nuc(l)),
    });
    return data ?? [];
  }, { concurrency: 1 });
}

const useDependencyPaths = ({ root, target }: UseDependencyPath): DependencyPathResponse => {
  const fetchDependencyPaths = useCallback(async () => {
    const { paths, unknownDependencies } = await fetchDependencyPathsAndUnknownDeps(root, target);
    // Grab all unique locators in list of paths
    const flattenedPaths = _.flattenDeep(paths);
    const allDepLocators = _.uniq(flattenedPaths.map(path => path.locator));

    const unknownDependencyMap = _.keyBy(unknownDependencies, ud => ud.locator);
    const currentRootUnknownDependency = unknownDependencyMap[target];

    if (!allDepLocators.length) {
      let currentPaths: Path[][] = [];
      let currentDependencyLocks: Record<string, MinimalDependencyLock> = {};
      // If there are unknown dep paths, return them
      if (currentRootUnknownDependency?.pathsTo) {
        currentPaths = currentRootUnknownDependency.pathsTo.paths.map(path => path.map(p => ({
          locator: p,
          title: renderPackage(p),
        }))) || [];
        currentDependencyLocks = unknownDependencyMap;
      }
      return {
        data: {
          paths: currentPaths,
          dependencyLocks: currentDependencyLocks,
        },
      };
    }

    // Otherwise, fetch dependency locks for all locators and return that
    const depLocksRaw = await fetchDependencyLocks(root, allDepLocators);

    // inject originPaths camelCase for consistency across API responses
    const depLocks = depLocksRaw?.map(depLock => ({
      ...depLock,
      originPaths: depLock.origin_paths,
    }));

    const dependencyLocksByLocator = _.keyBy(depLocks, dl => dl.locator);
    return {
      data: {
        paths: paths?.[0] || [],
        dependencyLocks: dependencyLocksByLocator,
      },
    };
  }, [root, target]);

  const { isLoading, value, errors } = useAsyncRequest(fetchDependencyPaths);

  const { paths, dependencyLocks } = value || {};

  return {
    dependencyLocks: dependencyLocks || {},
    isLoading,
    paths: paths || [],
    errors,
  };
};

export default useDependencyPaths;
